// Event folding (DESIGN_SPEC §12.5): the event stream as readable rows.
//   foldEvents(events, opts) → StreamRow[]   FoldedRow + epoch time, search text, pids and seqs
//     grouped   (ended runs) one row per signature across the run, ordered by first event; every pre-T0
//               non-background event becomes ONE setup row; a %TEMP%\tmp*.tmp burst of one pid, one churn row; one
//               pid running the same operations over MASS_FILES+ files in one burst (ransomware), one glob row
//     sequence  (and live) chronological; only consecutive repeats of a signature fold, within 30 s
//   Signatures: net.dns name (query, response and answers merge) · net.conn pid|dst · net.http method|host|path
//   · net.flow proto|dst|dport · file.* pid|path · registry.* kind|pid|key|value · image.load of C:\Windows\… per
//   pid · mutex name · screenshots. proc.start/exit, thread.inject, yara.match, dump.taken, verdict and job.state
//   never fold. A host flow mirroring a guest connection (same dst:port within 60 ms) folds into that row.
//   Every row names its process and pid; HTTP seen by the TLS proxy borrows the pid of the connection it rode on;
//   connections and flows name the host their address was resolved for.
//   rowFactory(events, opts) → (event) => StreamRow   one event as a row (expanded children, row detail)
//   link(events) → Links   DNS answer → query, mirrors, churn and mass groups, HTTP → pid, address → name
//   procResolver(events, sample?) → (pid, seq) => name · cadenceOf(times) · cadenceText(sec) · shortKey(key) · globOf(paths)
//   noiseOf(ctx) · namesOf(ctx) · t0Of(ctx) · phasesOf(ctx)   report-context helpers, memoized per job snapshot
import { bytes, count, envPath, winBase, winDir } from "@/lib/format";
import { categoryOf } from "@/lib/derive/categories";
import { classifyNoise, splitHostPort } from "@/lib/derive/noise";
import type { BodyPart, CiteIndex, FoldMode, FoldedRow, NoiseIndex, NoiseService, ReportCtx } from "@/lib/derive/types";
import type { Event } from "@/types";

type FoldOptions = {
  mode: FoldMode;
  /** Epoch ms of T0; null → the first event. */
  t0: number | null;
  noise?: NoiseIndex;
  /** Completed documents only: severities and technique ids for cited rows. */
  cite?: CiteIndex;
  /** Drop background network rows (they stay counted by the caller). */
  hideBackground?: boolean;
  /** Process name for a pid at a seq (the tree's view); defaults to procResolver(events). */
  procName?: (pid: number, seq: number) => string | undefined;
  /** The job's file name: names the agent's own launch record ({pid, target}). */
  sample?: string;
};

export type StreamRow = FoldedRow & {
  /** epoch ms of the first event (UTC gutter) */
  at: number;
  /** every seq on the row, the first included */
  seqs: number[];
  /** lowercase text the filter searches: full values, never the abbreviated display */
  hay: string;
  /** every pid the row involves (`pid:` filter) */
  pids: number[];
  /** the row's text in full (title attribute) */
  full: string;
  /** setup (pre-T0) · churn (%TEMP% burst, or one pid's mass file operations) · fold (repeats); absent on a single event */
  group?: "setup" | "churn" | "fold";
  service?: NoiseService;
  /** before T0 */
  pre: boolean;
};

const SEQ_WINDOW_MS = 30_000;
const MIRROR_MS = 60;
const CHURN_GAP_MS = 1000;
/** One pid running the same operations over this many files in one burst (ransomware, collectors) folds into a row. */
export const MASS_FILES = 16;
const ANSWER_MS = 2000;
const HTTP_CONN_MS = 2000;

const NEVER_FOLD = new Set(["proc.start", "proc.exit", "thread.inject", "yara.match", "config.extracted", "dump.taken", "verdict", "job.state"]);
const FILE_KINDS = new Set(["file.create", "file.write", "file.delete", "file.drop", "file.rename"]);
const TEMP_CHURN_RE = /^%TEMP%\\(?:[^\\]*\\)*tmp[^\\]*\.tmp$/i;
const SYSTEM_IMAGE_RE = /^[a-z]:\\windows\\/i;

export const str = (v: unknown): string => (typeof v === "string" ? v : v == null ? "" : String(v));
export function num(v: unknown): number | undefined {
  if (typeof v === "number") return Number.isFinite(v) ? v : undefined;
  if (typeof v === "string" && v.trim() !== "") {
    const n = Number(v);
    return Number.isFinite(n) ? n : undefined;
  }
  return undefined;
}
const lower = (s: string) => s.trim().toLowerCase().replace(/\.$/, "");
const pathKey = (p: string) => envPath(p.trim().replace(/\//g, "\\")).toLowerCase().replace(/\\+$/, "");
const defined = <T,>(xs: (T | undefined)[]): T[] => xs.filter((x): x is T => x !== undefined);

export function tsOf(e: Event, fallback: number): number {
  const t = Date.parse(e.ts);
  return Number.isFinite(t) ? t : fallback;
}

// ---- body parts ------------------------------------------------------------------------------

const ink = (text: string): BodyPart => ({ text });
const soft = (text: string): BodyPart => ({ text, tone: "soft" });
const mute = (text: string): BodyPart => ({ text, tone: "mute" });

/** "invoice_2026_scan.exe 4821" (name ink, pid faint); "pid 4821" when the name is unknown. */
function actor(name: string | undefined, pid: number | undefined): BodyPart[] {
  if (pid === undefined) return [ink(name || "unknown process")];
  if (!name) return [mute("pid "), { text: String(pid), tone: "faint", pid }];
  return [ink(name), { text: ` ${pid}`, tone: "faint", pid }];
}

/** A glob that covers the paths: "%USERPROFILE%\*\file_*", "%APPDATA%\WinSvc\*.dll". */
export function globOf(paths: readonly string[]): string {
  const ps = paths.map((p) => envPath(p.trim().replace(/\//g, "\\")).replace(/\\+$/, ""));
  if (!ps.length) return "*";
  const dirs = [...new Set(ps.map(winDir))];
  const names = ps.map(winBase);
  let prefix = names[0];
  for (const n of names) while (prefix && !n.toLowerCase().startsWith(prefix.toLowerCase())) prefix = prefix.slice(0, -1);
  const exts = new Set(names.map((n) => (/\.[^.]+$/.exec(n)?.[0] ?? "").toLowerCase()));
  const ext = exts.size === 1 ? [...exts][0] : "";
  const name = prefix === names[0] && names.every((n) => n.toLowerCase() === prefix.toLowerCase()) ? prefix : `${prefix}*${ext && !prefix.toLowerCase().endsWith(ext) ? ext : ""}`;
  if (dirs.length === 1) return `${dirs[0]}\\${name}`;
  const segs = dirs.map((d) => d.split("\\"));
  const common: string[] = [];
  for (let i = 0; segs.every((s) => s.length > i && s[i].toLowerCase() === segs[0][i].toLowerCase()); i++) common.push(segs[0][i]);
  const base = common.join("\\");
  if (!base) return `…\\${name}`;
  return `${base}\\${segs.every((s) => s.length === common.length + 1) ? "*" : "…"}\\${name}`;
}

/** "HKCU\Software\Microsoft\Windows\CurrentVersion\Run\WinSvc" → "HKCU\…\CurrentVersion\Run\WinSvc". */
export function shortKey(key: string): string {
  const parts = key.split("\\");
  return parts.length <= 5 ? key : [parts[0], "…", ...parts.slice(-3)].join("\\");
}

/** Median inter-arrival in seconds, given at least three occurrences; a burst (median under 50 ms) has none. */
export function cadenceOf(times: readonly number[]): number | undefined {
  if (times.length < 3) return undefined;
  const s = [...times].sort((a, b) => a - b);
  const gaps: number[] = [];
  for (let i = 1; i < s.length; i++) gaps.push(s[i] - s[i - 1]);
  gaps.sort((a, b) => a - b);
  const mid = gaps.length >> 1;
  const med = gaps.length % 2 ? gaps[mid] : (gaps[mid - 1] + gaps[mid]) / 2;
  return med >= 50 ? med / 1000 : undefined;
}

/** 1.94 → "every ~1.9 s" · 12.4 → "every ~12 s" · 150 → "every ~2.5 min". */
export function cadenceText(sec: number): string {
  if (sec < 10) return `every ~${sec.toFixed(1).replace(/\.0$/, "")}\u00a0s`;
  if (sec < 90) return `every ~${Math.round(sec)}\u00a0s`;
  return `every ~${(sec / 60).toFixed(1).replace(/\.0$/, "")}\u00a0min`;
}

/** exit 0 · exit 1 · NTSTATUS codes in hex (exit 0xC0000005). */
export function exitText(code: number | undefined): string {
  if (code === undefined) return "exit";
  return code > 0xffff || code < 0 ? `exit 0x${(code >>> 0).toString(16).toUpperCase().padStart(8, "0")}` : `exit ${code}`;
}

// ---- process names ---------------------------------------------------------------------------

/** pid → image basename at a seq, from proc.start (pid reuse aware) and the other events that name images. */
export function procResolver(events: readonly Event[], sample?: string): (pid: number, seq: number) => string | undefined {
  const starts = new Map<number, { seq: number; name: string }[]>();
  const other = new Map<number, string>();
  const note = (pid: number | undefined, image: unknown) => {
    const s = str(image);
    if (pid !== undefined && s && !other.has(pid)) other.set(pid, winBase(s));
  };
  for (const e of events) {
    const f = e.fields ?? {};
    switch (e.kind) {
      case "proc.start": {
        const pid = num(f.pid);
        if (pid === undefined) break;
        const image = str(f.image) || str(f.target);
        const list = starts.get(pid) ?? [];
        list.push({ seq: e.seq, name: image ? winBase(image) : sample || "" });
        starts.set(pid, list);
        note(num(f.ppid), f.parent_image);
        break;
      }
      case "proc.exit":
        note(num(f.pid), f.image);
        break;
      case "thread.inject":
        note(num(f.creator_pid), f.creator_image);
        note(num(f.target_pid), f.target_image);
        break;
      case "image.load":
        if (/\.exe$/i.test(str(f.image))) note(num(f.pid), f.image);
        break;
      default:
        break;
    }
  }
  return (pid, seq) => {
    const list = starts.get(pid);
    if (list?.length) {
      for (let i = list.length - 1; i >= 0; i--) if (list[i].seq <= seq && list[i].name) return list[i].name;
      const first = list.find((x) => x.name);
      if (first) return first.name;
    }
    return other.get(pid);
  };
}

// ---- links between events (one pass set per fold) -------------------------------------------------

type Links = {
  /** net.dns seq → the queried name it belongs to (answers carry CNAME targets) */
  dnsOf: Map<number, string>;
  /** host net.flow seq → the guest net.conn it mirrors */
  mirrorOf: Map<number, Event>;
  /** net.conn seqs with a mirrored host flow */
  mirrored: Set<number>;
  /** file event seq → churn group key (%TEMP%\tmp*.tmp, or one pid's mass file operations) */
  churnOf: Map<number, string>;
  /** net.http seq → the pid of the guest connection it rode on */
  httpPid: Map<number, number>;
  /** pid → proc.start times, oldest first (lifetimes on proc.exit) */
  starts: Map<number, number[]>;
  /** the name an address was last resolved for, at a time (epoch ms) */
  nameOf: (ip: string, at: number) => string | undefined;
  /** the guest's own address (flows from it omit the source) */
  guest?: string;
};

/** DNS answer → query, mirror flows, %TEMP% churn groups, HTTP → connection pid, process starts, the guest address. */
export function link(events: readonly Event[]): Links {
  const dnsOf = new Map<number, string>();
  const mirrorOf = new Map<number, Event>();
  const mirrored = new Set<number>();
  const churnOf = new Map<number, string>();
  const httpPid = new Map<number, number>();
  const starts = new Map<number, number[]>();
  type Conn = { at: number; e: Event; pid?: number; port?: number };
  const conns = new Map<string, Conn[]>();
  const connsByIp = new Map<string, Conn[]>();
  const ipsOf = new Map<string, Set<string>>();
  const answers = new Map<string, { name: string; at: number }[]>();
  const churn = new Map<number, { last: number; group: number }>();
  const churnMembers = new Map<string, { seqs: number[]; paths: Set<string> }>();
  const touches = new Map<number, { seq: number; at: number; id: string; op: string }[]>();
  const flows: { e: Event; at: number }[] = [];
  const https: { e: Event; at: number }[] = [];
  let guest: string | undefined;
  let lastQuery: { name: string; at: number } | null = null;
  let at = 0;

  for (const e of events) {
    at = tsOf(e, at);
    const f = e.fields ?? {};
    switch (e.kind) {
      case "log": {
        const m = /\bguest=([0-9a-f.:]+)/i.exec(str(f.msg));
        if (m) guest = m[1];
        break;
      }
      case "proc.start": {
        const pid = num(f.pid);
        if (pid !== undefined) (starts.get(pid) ?? starts.set(pid, []).get(pid)!).push(at);
        break;
      }
      case "net.dns": {
        const q = lower(str(f.qname));
        if (q) {
          lastQuery = { name: q, at };
          dnsOf.set(e.seq, q);
          break;
        }
        const name = lower(str(f.name));
        const owner = lastQuery && Math.abs(at - lastQuery.at) <= ANSWER_MS ? lastQuery.name : name;
        if (owner) dnsOf.set(e.seq, owner);
        const ip = str(f.ip).trim();
        if (ip && owner) {
          (ipsOf.get(owner) ?? ipsOf.set(owner, new Set()).get(owner)!).add(ip);
          (answers.get(ip) ?? answers.set(ip, []).get(ip)!).push({ name: owner, at });
        }
        break;
      }
      case "net.conn": {
        const { host, port } = splitHostPort(str(f.dst));
        const c: Conn = { at, e, pid: num(f.pid), port };
        const k = `${host}:${port ?? ""}`;
        (conns.get(k) ?? conns.set(k, []).get(k)!).push(c);
        (connsByIp.get(host) ?? connsByIp.set(host, []).get(host)!).push(c);
        break;
      }
      case "net.flow":
        flows.push({ e, at });
        break;
      case "net.http":
        https.push({ e, at });
        break;
      default:
        if (FILE_KINDS.has(e.kind)) {
          const pid = num(f.pid);
          if (pid !== undefined) {
            const id = pathKey(str(e.kind === "file.rename" ? f.from || f.to : f.path));
            (touches.get(pid) ?? touches.set(pid, []).get(pid)!).push({ seq: e.seq, at, id, op: e.kind.slice(5) });
          }
          const p = envPath(str(e.kind === "file.rename" ? f.to : f.path));
          if (pid === undefined || !TEMP_CHURN_RE.test(p)) break;
          let c = churn.get(pid);
          if (!c || at - c.last > CHURN_GAP_MS) churn.set(pid, (c = { last: at, group: (c?.group ?? -1) + 1 }));
          c.last = at;
          const key = `churn|${pid}|${c.group}`;
          const m = churnMembers.get(key) ?? { seqs: [], paths: new Set<string>() };
          m.seqs.push(e.seq);
          m.paths.add(p.toLowerCase());
          churnMembers.set(key, m);
        }
        break;
    }
  }
  // A churn group needs three distinct files; smaller bursts stay per-path rows.
  for (const [key, m] of churnMembers) if (m.paths.size >= 3) for (const s of m.seqs) churnOf.set(s, key);

  // Mass file operations: in one burst (gaps of at most 1 s) a pid runs the same operations over MASS_FILES or more
  // files: those files fold into one row; files it treated differently (a ransom note) keep their own rows.
  for (const [pid, list] of touches) {
    let run: typeof list = [];
    let n = 0;
    const settle = () => {
      if (run.length >= MASS_FILES) {
        const byId = new Map<string, { ops: string[]; seqs: number[] }>();
        for (const t of run) {
          const x = byId.get(t.id) ?? { ops: [], seqs: [] };
          if (x.ops[x.ops.length - 1] !== t.op) x.ops.push(t.op);
          x.seqs.push(t.seq);
          byId.set(t.id, x);
        }
        const bySig = new Map<string, string[]>();
        for (const [id, x] of byId) {
          const s = x.ops.join(">");
          (bySig.get(s) ?? bySig.set(s, []).get(s)!).push(id);
        }
        let best: [string, string[]] | null = null;
        for (const e of bySig) if (!best || e[1].length > best[1].length) best = e;
        if (best && best[1].length >= MASS_FILES) for (const id of best[1]) for (const s of byId.get(id)!.seqs) churnOf.set(s, `mass|${pid}|${n}|${best[0]}`);
      }
      run = [];
      n++;
    };
    for (const t of list) {
      if (churnOf.has(t.seq)) continue;
      if (run.length && t.at - run[run.length - 1].at > CHURN_GAP_MS) settle();
      run.push(t);
    }
    settle();
  }

  // Mirror: a host flow to the same dst:port within 60 ms of a guest connection; each connection takes one flow.
  for (const { e, at: fat } of flows) {
    const f = e.fields ?? {};
    const { host, port } = splitHostPort(str(f.dst));
    const list = conns.get(`${host}:${num(f.dport) ?? port ?? ""}`);
    if (!list) continue;
    let best: { c: Conn; d: number } | null = null;
    for (const c of list) {
      const d = Math.abs(c.at - fat);
      if (d <= MIRROR_MS && !mirrored.has(c.e.seq) && (!best || d < best.d)) best = { c, d };
    }
    if (best) {
      mirrored.add(best.c.e.seq);
      mirrorOf.set(e.seq, best.c.e);
    }
  }

  // HTTP through the proxy: the closest guest connection to an address the name resolved to.
  for (const { e, at: hat } of https) {
    const f = e.fields ?? {};
    const { host, port } = splitHostPort(str(f.host));
    const h = lower(host);
    const want = port ?? (str(f.scheme) === "https" ? 443 : str(f.scheme) === "http" ? 80 : undefined);
    const ips = ipsOf.get(h) ?? (/^[\d.]+$/.test(h) || h.includes(":") ? new Set([h]) : undefined);
    if (!ips) continue;
    let best: { pid: number; score: number } | null = null;
    for (const ip of ips) {
      for (const c of connsByIp.get(ip) ?? []) {
        const d = Math.abs(c.at - hat);
        if (c.pid === undefined || d > HTTP_CONN_MS) continue;
        const score = d + (c.port === want ? 0 : 1000);
        if (!best || score < best.score) best = { pid: c.pid, score };
      }
    }
    if (best) httpPid.set(e.seq, best.pid);
  }
  const nameOf = (ip: string, when: number): string | undefined => {
    const list = answers.get(ip);
    if (!list?.length) return undefined;
    let best = list[0];
    for (const a of list) if (a.at <= when + 1000) best = a;
    return best.name;
  };
  return { dnsOf, mirrorOf, mirrored, churnOf, httpPid, starts, nameOf, guest };
}

// ---- signatures -----------------------------------------------------------------------------------

function connSig(e: Event): string {
  const f = e.fields ?? {};
  return `conn|${str(f.pid)}|${str(f.dst).toLowerCase()}|${str(f.op)}`;
}

/** `own`: the event's own signature (no mirror or churn grouping), for single-event rows. */
function sigOf(e: Event, links: Links, setup: boolean, own = false): string {
  const f = e.fields ?? {};
  if (setup) return "setup";
  if (NEVER_FOLD.has(e.kind)) return `one|${e.seq}`;
  switch (e.kind) {
    case "net.dns":
      return `dns|${links.dnsOf.get(e.seq) ?? lower(str(f.qname ?? f.name))}`;
    case "net.conn":
      return connSig(e);
    case "net.http":
      return `http|${str(f.method).toUpperCase()}|${lower(str(f.host))}|${str(f.path)}`;
    case "net.flow": {
      const mirror = own ? undefined : links.mirrorOf.get(e.seq);
      if (mirror) return connSig(mirror);
      const { host, port } = splitHostPort(str(f.dst));
      return `flow|${str(f.proto).toLowerCase()}|${host.toLowerCase()}|${num(f.dport) ?? port ?? ""}`;
    }
    case "registry.set":
    case "registry.delete":
      return `reg|${e.kind}|${str(f.pid)}|${str(f.key).toLowerCase()}|${str(f.value)}`;
    case "image.load": {
      const img = str(f.image);
      return SYSTEM_IMAGE_RE.test(img) && !own ? `load|${str(f.pid)}|sys` : `load|${str(f.pid)}|${pathKey(img)}`;
    }
    case "mutex.create":
      return `mutex|${str(f.name)}`;
    case "log":
      return `log|${str(f.msg)}`;
    case "artifact":
      return `artifact|${str(f.kind)}|${str(f.name)}`;
    case "screenshot":
    case "agent.hello":
    case "agent.lost":
      return e.kind;
    default:
      if (FILE_KINDS.has(e.kind)) return (own ? undefined : links.churnOf.get(e.seq)) ?? `file|${str(f.pid)}|${pathKey(str(e.kind === "file.rename" ? f.to : f.path))}`;
      return `one|${e.seq}`;
  }
}

/** The kind a signature describes: a connection row stays a connection even when its mirror flow came first. */
function kindOfSig(sig: string, e: Event): string {
  if (sig === "setup") return "setup";
  if (sig.startsWith("conn|")) return "net.conn";
  if (sig.startsWith("churn|")) return "file.churn";
  if (sig.startsWith("mass|")) return "file.mass";
  return e.kind;
}

// ---- accumulators and description --------------------------------------------------------------------

type Acc = {
  sig: string;
  kind: string;
  first: Event;
  at: number;
  last: number;
  seqs: number[];
  events: Event[];
  times: number[];
  bg: number;
  services: Map<NoiseService, number>;
};

function newAcc(sig: string, e: Event, at: number): Acc {
  return { sig, kind: kindOfSig(sig, e), first: e, at, last: at, seqs: [], events: [], times: [], bg: 0, services: new Map() };
}

function addTo(acc: Acc, e: Event, at: number, service: NoiseService | undefined) {
  acc.seqs.push(e.seq);
  acc.events.push(e);
  acc.times.push(at);
  if (at > acc.last) acc.last = at;
  if (service) {
    acc.bg++;
    acc.services.set(service, (acc.services.get(service) ?? 0) + 1);
  }
}

type Ctx = {
  name: (pid: number | undefined, seq: number) => string | undefined;
  links: Links;
  t0: number;
  mode: FoldMode;
};

type Described = { parts: BodyPart[]; extra?: string; pids: number[]; bytes?: number; cadenceSec?: number; count?: number };

function describe(acc: Acc, d: Ctx): Described {
  const rep = acc.events.find((x) => x.kind === acc.kind) ?? acc.first;
  const f = rep.fields ?? {};
  const n = acc.events.length;
  const pidOf = (x: Event) => num(x.fields?.pid);
  const who = (x: Event) => actor(d.name(pidOf(x), x.seq), pidOf(x));
  const timesOf = (kind: string) => acc.events.map((x, i) => (x.kind === kind ? acc.times[i] : NaN)).filter(Number.isFinite);
  const cad = (sec?: number): BodyPart[] => (sec ? [mute(` · ${cadenceText(sec)}`)] : []);

  switch (acc.kind) {
    case "setup": {
      const states: string[] = [];
      const notes: string[] = [];
      for (const x of acc.events) {
        if (x.kind === "job.state") {
          const s = str(x.fields?.state);
          if (s && states[states.length - 1] !== s) states.push(s);
        } else if (x.kind === "log") {
          const m = str(x.fields?.msg).trim();
          if (m && !notes.includes(m)) notes.push(m);
        }
      }
      const parts: BodyPart[] = [];
      if (states.length) parts.push(ink(states.join(" › ")));
      if (notes.length) parts.push(mute(`${parts.length ? " · " : ""}${notes.join(" · ")}`));
      if (!parts.length) parts.push(ink(`${n} setup ${n === 1 ? "event" : "events"}`));
      return { parts, pids: [] };
    }
    case "file.churn": {
      const files = new Set<string>();
      const ops = new Map<string, number>();
      for (const x of acc.events) {
        files.add(pathKey(str(x.kind === "file.rename" ? x.fields?.to : x.fields?.path)));
        const op = x.kind.slice(5);
        ops.set(op, (ops.get(op) ?? 0) + 1);
      }
      const span = (acc.last - acc.at) / 1000;
      const opText = [...ops].map(([op, k]) => `${op} ${k}`).join(" · ");
      return {
        parts: [...who(rep), mute(" · "), ink("%TEMP%\\tmp*.tmp"), mute(` · ${files.size} files · ${opText} in ${span.toFixed(1)}\u00a0s`)],
        extra: acc.events.map((x) => str(x.fields?.path ?? x.fields?.to)).join(" "),
        pids: defined([pidOf(rep)]),
      };
    }
    case "file.mass": {
      const paths = new Map<string, string>();
      const ops = new Map<string, number>();
      const suffixes = new Set<string>();
      let written = 0;
      for (const x of acc.events) {
        const xf = x.fields ?? {};
        const from = str(xf.from);
        const p = str(x.kind === "file.rename" ? from || xf.to : xf.path);
        paths.set(pathKey(p), p);
        const op = x.kind.slice(5);
        ops.set(op, (ops.get(op) ?? 0) + 1);
        written += num(xf.bytes) ?? 0;
        if (x.kind === "file.rename") {
          const to = str(xf.to);
          suffixes.add(from && to.toLowerCase().startsWith(from.toLowerCase()) ? to.slice(from.length) : "?");
        }
      }
      const files = [...paths.values()];
      const dirs = new Set(files.map((p) => winDir(envPath(p)).toLowerCase())).size;
      const suffix = suffixes.size === 1 ? [...suffixes][0] : "";
      const opText = [...ops].map(([op, k]) => (op === "rename" && suffix && suffix !== "?" ? `rename ${k} to *${suffix}` : `${op} ${k}`)).join(" · ");
      const span = (acc.last - acc.at) / 1000;
      return {
        parts: [
          ...who(rep),
          mute(" · "),
          ink(globOf(files)),
          mute(` · ${count(files.length)} files${dirs > 1 ? ` in ${dirs} folders` : ""} · ${opText}${written ? ` · ${bytes(written)} written` : ""} in ${span.toFixed(1)} s`),
        ],
        extra: files.join(" "),
        pids: defined([pidOf(rep)]),
        bytes: written || undefined,
      };
    }
    case "job.state": {
      const reason = str(f.reason);
      const isT0 = Math.abs(acc.at - d.t0) < 1;
      return { parts: [ink(str(f.state) || "state"), ...(isT0 ? [mute(" · T0")] : []), ...(reason ? [mute(` · ${reason}`)] : [])], pids: [] };
    }
    case "proc.start": {
      const pid = num(f.pid);
      const ppid = num(f.ppid);
      const image = str(f.image) || str(f.target);
      const parts: BodyPart[] = [...actor(image ? winBase(image) : d.name(pid, rep.seq), pid)];
      const parent = str(f.parent_image) ? winBase(str(f.parent_image)) : ppid !== undefined ? d.name(ppid, rep.seq) : undefined;
      if (ppid !== undefined && parent) parts.push(mute(" ‹ "), ...actor(parent, ppid));
      else if (!str(f.image) && str(f.target)) parts.push(mute(" · launched by the guest agent"));
      const cmd = str(f.cmdline).trim();
      if (cmd) {
        const quoted = cmd.startsWith('"');
        const end = quoted ? cmd.indexOf('"', 1) : cmd.search(/\s/);
        const argv0 = end < 0 ? cmd.slice(quoted ? 1 : 0) : cmd.slice(quoted ? 1 : 0, end);
        const rest = end < 0 ? "" : cmd.slice(end + (quoted ? 1 : 0)).trim();
        const same = !!image && (argv0.toLowerCase() === image.toLowerCase() || winBase(argv0).toLowerCase() === winBase(image).toLowerCase());
        const args = same ? rest : cmd;
        if (args) parts.push(mute(" · "), soft(args));
      }
      return { parts, extra: `${image} ${cmd}`, pids: defined([pid, ppid]) };
    }
    case "proc.exit": {
      const pid = num(f.pid);
      const started = pid !== undefined ? d.links.starts.get(pid)?.filter((s) => s <= acc.at).pop() : undefined;
      const life = started !== undefined ? ` · after ${((acc.at - started) / 1000).toFixed(2)}\u00a0s` : "";
      const name = str(f.image) ? winBase(str(f.image)) : d.name(pid, rep.seq);
      return { parts: [...actor(name, pid), mute(` ${exitText(num(f.exit_code))}${life}`)], pids: defined([pid]) };
    }
    case "thread.inject": {
      const cpid = num(f.creator_pid);
      const tpid = num(f.target_pid);
      const cname = str(f.creator_image) ? winBase(str(f.creator_image)) : d.name(cpid, rep.seq);
      const tname = str(f.target_image) ? winBase(str(f.target_image)) : d.name(tpid, rep.seq);
      const tid = str(f.tid);
      const start = typeof f.start_addr === "number" ? `0x${f.start_addr.toString(16)}` : str(f.start_addr);
      return {
        parts: [...actor(cname, cpid), mute(" › "), ...actor(tname, tpid), mute(`${tid ? ` · tid ${tid}` : ""}${start ? ` · start ${start}` : ""}`)],
        pids: defined([cpid, tpid]),
      };
    }
    case "dump.taken": {
      const size = num(f.size);
      const reason = str(f.reason);
      const extra = [str(f.name), size !== undefined ? bytes(size) : "", reason ? `on ${reason}` : ""].filter(Boolean).join(" · ");
      return { parts: [...who(rep), ...(extra ? [mute(` · ${extra}`)] : [])], pids: defined([pidOf(rep)]) };
    }
    case "yara.match": {
      const kind = str(f.kind).replace(/-/g, " ");
      return { parts: [ink(str(f.rule) || "rule"), mute(" › "), ink(str(f.artifact) || "artifact"), ...(kind ? [mute(` · ${kind}`)] : [])], pids: [] };
    }
    case "config.extracted": {
      const list = (v: unknown) => (Array.isArray(v) ? v.map(str).filter(Boolean) : []);
      const c2 = list(f.c2);
      const urls = list(f.urls);
      const mutex = list(f.mutex);
      const conf = num(f.confidence);
      const tail = [
        c2.length > 1 ? `+${count(c2.length - 1)} C2` : "",
        urls.length ? `${count(urls.length)} ${urls.length === 1 ? "url" : "urls"}` : "",
        mutex.length ? `${count(mutex.length)} ${mutex.length === 1 ? "mutex" : "mutexes"}` : "",
        conf ? `confidence ${conf}` : "",
      ].filter(Boolean);
      return {
        parts: [
          ink(str(f.family) || "config"),
          mute(` · ${str(f.extractor) || "extractor"}`),
          ...(c2[0] ? [mute(" › "), ink(c2[0])] : []),
          ...(tail.length ? [mute(` · ${tail.join(" · ")}`)] : []),
        ],
        pids: [],
      };
    }
    case "verdict": {
      const score = num(f.score);
      const tags = num(f.tag_count);
      return {
        parts: [ink(str(f.label) || "verdict"), mute(`${score !== undefined ? ` · score ${score}` : ""}${tags !== undefined ? ` · ${count(tags)} ${tags === 1 ? "tag" : "tags"}` : ""}`)],
        pids: [],
      };
    }
    case "net.dns": {
      if (n === 1 && str(f.qname)) {
        const qr = f.qr === true || f.qr === "true";
        return { parts: [mute(qr ? "response " : "query "), ink(lower(str(f.qname))), mute(` ${str(f.qtype) || "A"}`)], pids: [] };
      }
      const qname = acc.sig.startsWith("dns|") ? acc.sig.slice(4) : lower(str(f.qname ?? f.name));
      let lookups = 0;
      let qtype = "";
      const ips: string[] = [];
      const via: string[] = [];
      const qtimes: number[] = [];
      acc.events.forEach((x, i) => {
        const xf = x.fields ?? {};
        if (str(xf.qname)) {
          if (xf.qr !== true && xf.qr !== "true") {
            lookups++;
            qtimes.push(acc.times[i]);
          }
          qtype ||= str(xf.qtype);
          return;
        }
        const ip = str(xf.ip).trim();
        if (ip && !ips.includes(ip)) ips.push(ip);
        const name = lower(str(xf.name));
        if (name && name !== qname && !via.includes(name)) via.push(name);
        qtype ||= str(xf.rtype);
      });
      const lk = Math.max(1, lookups);
      const c = lk >= 3 ? cadenceOf(qtimes) : undefined;
      return {
        parts: [
          ink(qname || "?"),
          mute(` ${qtype || "A"} › `),
          ips.length ? ink(ips.join(", ")) : mute(lookups ? "no answer" : "answer"),
          ...(via.length ? [mute(` · via ${via.join(", ")}`)] : []),
          ...cad(c),
        ],
        pids: [],
        cadenceSec: c,
        count: lk,
      };
    }
    case "net.conn": {
      const conns = acc.events.filter((x) => x.kind === "net.conn");
      const mirrored = conns.some((x) => d.links.mirrored.has(x.seq));
      const c = conns.length >= 3 ? cadenceOf(timesOf("net.conn")) : undefined;
      const name = d.links.nameOf(splitHostPort(str(f.dst)).host, acc.at);
      return {
        parts: [
          ...who(rep),
          mute(` ${str(f.op) || "connect"} ${str(f.proto) || "tcp"} › `),
          ink(str(f.dst)),
          ...(name ? [soft(` ${name}`)] : []),
          ...(mirrored ? [mute(" · host capture too")] : []),
          ...cad(c),
        ],
        pids: defined([pidOf(rep)]),
        cadenceSec: c,
        count: Math.max(1, conns.length),
      };
    }
    case "net.http": {
      const pids = [...new Set(defined(acc.events.map((x) => d.links.httpPid.get(x.seq))))];
      const statuses = [...new Set(acc.events.map((x) => str(x.fields?.status)).filter(Boolean))];
      let up = 0;
      let down = 0;
      let sized = false;
      for (const x of acc.events) {
        const r = num(x.fields?.req_bytes);
        const s = num(x.fields?.resp_bytes);
        if (r !== undefined || s !== undefined) sized = true;
        up += r ?? 0;
        down += s ?? 0;
      }
      const scheme = str(f.scheme);
      const c = n >= 3 ? cadenceOf(acc.times) : undefined;
      const lead: BodyPart[] = pids.length ? [...actor(d.name(pids[0], rep.seq), pids[0]), mute(" · ")] : [mute("TLS proxy · ")];
      return {
        parts: [
          ...lead,
          ink(`${str(f.method).toUpperCase() || "GET"} ${scheme ? `${scheme}://` : ""}${str(f.host)}${str(f.path)}`),
          ...(statuses.length ? [mute(` › ${statuses.join(", ")}`)] : []),
          ...(sized ? [mute(` · ↑\u00a0${bytes(up)} ↓\u00a0${bytes(down)}`)] : []),
          ...cad(c),
        ],
        pids,
        cadenceSec: c,
        bytes: sized ? up + down : undefined,
      };
    }
    case "net.flow": {
      const { host, port } = splitHostPort(str(f.dst));
      const dport = num(f.dport) ?? port;
      const src = str(f.src).trim();
      let total = 0;
      let sized = false;
      for (const x of acc.events) {
        const l = num(x.fields?.len);
        if (l !== undefined) {
          total += l;
          sized = true;
        }
      }
      const c = n >= 3 ? cadenceOf(acc.times) : undefined;
      const showSrc = !!src && src !== d.links.guest && src !== "0.0.0.0";
      const name = d.links.nameOf(host, acc.at);
      return {
        parts: [
          mute(`host capture · ${str(f.proto) || "ip"} `),
          ...(showSrc ? [soft(src), mute(" › ")] : [mute("› ")]),
          ink(`${host.includes(":") ? `[${host}]` : host}${dport !== undefined ? `:${dport}` : ""}`),
          ...(name ? [soft(` ${name}`)] : []),
          ...(sized ? [mute(` · ${bytes(total)}`)] : []),
          ...cad(c),
        ],
        pids: [],
        cadenceSec: c,
        bytes: sized ? total : undefined,
      };
    }
    case "registry.set":
    case "registry.delete": {
      const key = str(f.key);
      const value = str(f.value);
      const del = acc.kind === "registry.delete";
      return {
        parts: [...who(rep), mute(del ? " · delete " : " · "), ink(shortKey(key)), ...(value && !del ? [mute(" = "), soft(envPath(value))] : [])],
        extra: `${key} ${value}`,
        pids: defined([pidOf(rep)]),
      };
    }
    case "image.load": {
      if (acc.sig.endsWith("|sys") && n > 1) {
        const names = [...new Set(acc.events.map((x) => winBase(str(x.fields?.image))))];
        return {
          parts: [...who(rep), mute(` · ${names.length} system ${names.length === 1 ? "module" : "modules"} · `), soft(`${names.slice(0, 4).join(" ")}${names.length > 4 ? " …" : ""}`)],
          extra: acc.events.map((x) => str(x.fields?.image)).join(" "),
          pids: defined([pidOf(rep)]),
        };
      }
      return { parts: [...who(rep), mute(" · "), ink(envPath(str(f.image)))], extra: str(f.image), pids: defined([pidOf(rep)]) };
    }
    case "mutex.create":
      return { parts: [...who(rep), mute(" · "), ink(str(f.name))], pids: [...new Set(defined(acc.events.map(pidOf)))] };
    case "log":
      return { parts: [soft(str(f.msg) || "log")], pids: [] };
    case "agent.hello":
      return { parts: [ink("guest helper connected"), ...(str(f.protocol) ? [mute(` · protocol ${str(f.protocol)}`)] : [])], pids: [] };
    case "agent.lost":
      return { parts: [ink("guest helper lost"), ...(str(f.reason) ? [mute(` · ${str(f.reason)}`)] : [])], pids: [] };
    case "artifact": {
      const size = num(f.size);
      const extra = [str(f.name), size !== undefined ? bytes(size) : "", str(f.imphash) ? `imphash ${str(f.imphash)}` : ""].filter(Boolean).join(" · ");
      return { parts: [ink(str(f.kind) || "artifact"), ...(extra ? [mute(` · ${extra}`)] : [])], pids: [] };
    }
    case "screenshot": {
      const names = acc.events.map((x) => str(x.fields?.name)).filter(Boolean);
      const range = names.length > 1 ? `${names[0]} … ${names[names.length - 1]}` : (names[0] ?? "frame");
      return n > 1 && d.mode === "grouped" ? { parts: [ink(`${n} frames`), mute(` · ${range}`)], pids: [] } : { parts: [ink(range)], pids: [] };
    }
    default: {
      if (FILE_KINDS.has(acc.kind)) {
        const ops: string[] = [];
        let written = 0;
        let sized = false;
        let from = "";
        for (const x of acc.events) {
          const b = num(x.fields?.bytes);
          if (b !== undefined) {
            written += b;
            sized = true;
          }
          if (x.kind === "file.rename") from ||= str(x.fields?.from);
          const op = x.kind.slice(5);
          if (ops[ops.length - 1] !== op) ops.push(op);
        }
        const raw = str(acc.kind === "file.rename" ? f.to : f.path);
        return {
          parts: [...who(rep), mute(" · "), ink(envPath(raw) || "?"), mute(` · ${ops.join(" › ")}${sized ? ` ${count(written)}\u00a0B` : ""}`), ...(from ? [mute(` from ${envPath(from)}`)] : [])],
          extra: `${raw} ${from}`,
          pids: defined([pidOf(rep)]),
          bytes: sized ? written : undefined,
        };
      }
      const pairs = Object.entries(f)
        .slice(0, 8)
        .map(([k, v]) => `${k}=${typeof v === "object" ? JSON.stringify(v) : str(v)}`)
        .join(" ");
      return { parts: [soft(pairs || rep.kind)], pids: defined([pidOf(rep)]) };
    }
  }
}

function toRow(acc: Acc, d: Ctx, cite: CiteIndex | undefined): StreamRow {
  const desc = describe(acc, d);
  const n = acc.events.length;
  let sev: 3 | 4 | 5 | undefined;
  let mitre: string | undefined;
  if (cite) {
    let best = 0;
    for (const s of acc.seqs) {
      const c = cite.get(s);
      if (c && c.sev > best) {
        best = c.sev;
        mitre = c.mitre;
      }
    }
    if (best >= 3) sev = best as 3 | 4 | 5;
    else mitre = undefined;
  }
  let service: NoiseService | undefined;
  let top = 0;
  for (const [s, k] of acc.services) if (k > top) [service, top] = [s, k];
  const background = acc.bg > 0 && acc.bg === n;
  const parts: BodyPart[] = background && service ? [...desc.parts, { text: ` · ${service}`, tone: "faint" }] : desc.parts;
  const full = parts.map((p) => p.text).join("");
  const setup = acc.kind === "setup";
  return {
    key: setup ? "setup" : `r${acc.first.seq}`,
    seq: acc.first.seq,
    t: acc.at - d.t0,
    at: acc.at,
    category: setup ? "system" : categoryOf(acc.kind === "file.churn" || acc.kind === "file.mass" ? "file.write" : acc.kind),
    kind: acc.kind === "file.churn" || acc.kind === "file.mass" ? acc.first.kind : acc.kind,
    parts,
    count: desc.count ?? n,
    cadenceSec: desc.cadenceSec,
    bytes: desc.bytes,
    sev,
    mitre,
    pid: desc.pids[0],
    children: n > 1 ? acc.seqs : undefined,
    background,
    seqs: acc.seqs,
    hay: `${setup ? "setup" : acc.kind} ${full} ${desc.extra ?? ""} ${desc.pids.join(" ")}`.toLowerCase(),
    pids: desc.pids,
    full,
    group: setup ? "setup" : acc.kind === "file.churn" || acc.kind === "file.mass" ? "churn" : n > 1 ? "fold" : undefined,
    service: background ? service : undefined,
    pre: acc.at - d.t0 < 0,
  };
}

// ---- the fold --------------------------------------------------------------------------------------

export function foldEvents(events: readonly Event[], opts: FoldOptions): StreamRow[] {
  if (!events.length) return [];
  const grouped = opts.mode === "grouped";
  const firstAt = tsOf(events[0], 0);
  const t0 = opts.t0 ?? firstAt;
  const links = link(events);
  const procName = opts.procName ?? procResolver(events, opts.sample);
  const accs: Acc[] = [];
  const bySig = new Map<string, Acc>();
  let at = firstAt;

  for (const e of events) {
    at = tsOf(e, at);
    const hit = opts.noise?.hits.get(e.seq);
    if (hit && opts.hideBackground) continue;
    const sig = sigOf(e, links, grouped && at < t0 && !hit);
    let acc: Acc | undefined;
    if (grouped) acc = sig.startsWith("one|") ? undefined : bySig.get(sig);
    else {
      const prev = accs[accs.length - 1];
      if (prev && prev.sig === sig && !sig.startsWith("one|") && at - prev.at <= SEQ_WINDOW_MS) acc = prev;
    }
    if (!acc) {
      acc = newAcc(sig, e, at);
      accs.push(acc);
      if (grouped) bySig.set(sig, acc);
    }
    addTo(acc, e, at, hit?.service);
  }

  const d: Ctx = { name: (pid, seq) => (pid === undefined ? undefined : procName(pid, seq)), links, t0, mode: opts.mode };
  return accs.map((acc) => toRow(acc, d, opts.cite));
}

export type RowFactory = (e: Event) => StreamRow;

/** One event as its own row, in the fold's grammar: expanded children and the row detail. */
export function rowFactory(events: readonly Event[], opts: Omit<FoldOptions, "mode" | "hideBackground">): RowFactory {
  const links = link(events);
  const procName = opts.procName ?? procResolver(events, opts.sample);
  const t0 = opts.t0 ?? (events.length ? tsOf(events[0], 0) : 0);
  const d: Ctx = { name: (pid, seq) => (pid === undefined ? undefined : procName(pid, seq)), links, t0, mode: "sequence" };
  return (e) => {
    const at = tsOf(e, t0);
    const acc = newAcc(sigOf(e, links, false, true), e, at);
    addTo(acc, e, at, opts.noise?.hits.get(e.seq)?.service);
    const row = toRow(acc, d, opts.cite);
    return { ...row, key: `e${e.seq}`, group: undefined };
  };
}

// ---- report context helpers (memoized per job on snap.version) -----------------------------------------

const NOISE = new Map<string, { version: number; noise: NoiseIndex }>();
const NAMES = new Map<string, { version: number; sample?: string; name: (pid: number, seq: number) => string | undefined }>();

function remember<T>(m: Map<string, T>, id: string, v: T): T {
  m.delete(id);
  m.set(id, v);
  if (m.size > 8) m.delete(m.keys().next().value!);
  return v;
}

/** The shared background index (the report's, else computed once per snapshot). */
export function noiseOf(ctx: ReportCtx): NoiseIndex {
  if (ctx.noise) return ctx.noise;
  const v = ctx.stream.snap.version;
  const m = NOISE.get(ctx.job.id);
  if (m && m.version === v) return m.noise;
  return remember(NOISE, ctx.job.id, { version: v, noise: classifyNoise(ctx.stream.snap.events) }).noise;
}

/** pid → process name at a seq, once per snapshot. */
export function namesOf(ctx: ReportCtx): (pid: number, seq: number) => string | undefined {
  const v = ctx.stream.snap.version;
  const sample = ctx.job.filename;
  const m = NAMES.get(ctx.job.id);
  if (m && m.version === v && m.sample === sample) return m.name;
  return remember(NAMES, ctx.job.id, { version: v, sample, name: procResolver(ctx.stream.snap.events, sample) }).name;
}

/** T0 (epoch ms): the stream's, else the first event's. */
export function t0Of(ctx: ReportCtx): number | null {
  const ev = ctx.stream.snap.events;
  return ctx.stream.t0 ?? (ev.length ? tsOf(ev[0], NaN) : null);
}

const PHASE_LABEL: Partial<Record<string, string>> = { booting: "Boot", running: "Run", collecting: "Collect", cancelled: "Cancel", failed: "Fail" };

/** BOOT / RUN / COLLECT (and a cancel or failure) as ms from T0, for the lane chart: the stream's own steps only
 *  (a step only the record knows carries the record's clock, not the guest's). */
export function phasesOf(ctx: ReportCtx): { label: string; t: number }[] {
  const t0 = t0Of(ctx);
  if (t0 === null) return [];
  const out: { label: string; t: number }[] = [];
  for (const tr of ctx.stream.transitions) {
    const label = PHASE_LABEL[tr.state];
    if (label && tr.source !== "record" && Number.isFinite(tr.ts) && !out.some((p) => p.label === label)) out.push({ label, t: tr.ts - t0 });
  }
  return out;
}
